import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Power } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, Drawer, Panel, money, eurOf, useEurRate } from "./ui";
import ImageCropModal from "../../components/ImageCropModal";

const LOCALES = ["en", "fr", "es", "ar"];
const EMPTY = {
  name: { en: "", fr: "", es: "", ar: "" }, description: {},
  price_eur: 0, cost_price_mad: 0, fulfilled_by: "platform", image_url: "",
  is_celebratory: false, is_active: true, city_ids: [], occasion_ids: [], sort_order: 100,
};

const Chips = ({ options, selected, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
    {options.map((o) => {
      const on = selected.includes(o.id);
      return (
        <button key={o.id} type="button" data-testid={`${testid}-${o.id}`}
                className={`text-[11px] border px-2 py-1 transition-colors ${on ? "bg-ink text-plaster border-ink" : "border-brass-light text-muted hover:border-brass"}`}
                onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}>
          {o.name}
        </button>
      );
    })}
  </div>
);

// Fleurinelle's own extras (chocolates, cake, vase…): cost vs selling price, who fulfils them, where they show.
export default function ExtrasPage() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const fx = useEurRate();

  const load = useCallback(() => api.get("/admin/extras").then((r) => setData(r.data)).catch((e) => setMsg(errText(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg("");
    const body = { ...draft, price_eur: Number(draft.price_eur), cost_price_mad: Number(draft.cost_price_mad) };
    try {
      if (draft.id) await api.put(`/admin/extras/${draft.id}`, body); else await api.post("/admin/extras", body);
      setDraft(null); setMsg("Saved."); load();
    } catch (e) { setMsg(errText(e)); }
  };
  const toggle = async (row) => {
    try { await api.post(`/admin/extras/${row.id}/toggle`, { is_active: !row.is_active }); load(); }
    catch (e) { setMsg(errText(e)); }
  };
  const remove = async (row) => {
    try { await api.delete(`/admin/extras/${row.id}`); load(); } catch (e) { setMsg(errText(e)); }
  };
  const upload = async (file) => {
    const form = new FormData();
    form.append("photo", file);
    try {
      const { data: r } = await api.post("/admin/extras/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, image_url: r.url }));
      setPendingPhoto(null);
    } catch (e) { setMsg(errText(e)); setPendingPhoto(null); }
  };

  if (!data) return <p className="text-sm text-muted" data-testid="extras-loading">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="admin-extras-page">
      <div>
        <h1 className="font-display text-3xl text-ink">Extras & add-ons</h1>
        <p className="text-sm text-muted max-w-2xl">
          Your own extras, sold alongside every bouquet. Set what the buyer pays and what the fulfiller is paid, choose
          whether Fleurinelle or the partner florist supplies it, and limit it to certain cities or occasions.
          Extras are never discounted — a coupon only ever touches the bouquet price.
        </p>
      </div>
      {msg ? <p className="text-sm text-brass" data-testid="extras-message">{msg}</p> : null}

      <Panel title="Platform extras" testid="extras-panel"
             action={<button className="btn-primary text-xs px-3 inline-flex items-center gap-1" data-testid="extra-new"
                             onClick={() => setDraft({ ...EMPTY })}><Plus size={13} /> New extra</button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="smallcaps border-b border-brass-light">
              <th className="p-2 text-start">Extra</th><th className="p-2">Buyer pays</th><th className="p-2">Cost</th>
              <th className="p-2">Margin</th><th className="p-2 text-start">Fulfilled by</th><th className="p-2 text-start">Shown on</th>
              <th className="p-2">Sold</th><th className="p-2">Status</th><th className="p-2" />
            </tr></thead>
            <tbody>
              {data.extras.map((a) => (
                <tr key={a.id} className="border-b border-brass-light/60" data-testid={`extra-row-${a.id}`}>
                  <td className="p-2">
                    <button className="underline decoration-brass underline-offset-2 text-start" data-testid={`extra-edit-${a.id}`}
                            onClick={() => setDraft({ ...EMPTY, ...a, name: { ...EMPTY.name, ...(a.name || {}) } })}>
                      {a.name?.en || "—"}
                    </button>
                    {a.is_celebratory ? <span className="text-[10px] text-muted ms-2">celebratory</span> : null}
                  </td>
                  <td className="p-2 text-center tabular" data-testid={`extra-retail-${a.id}`}>€{Number(a.price_eur || 0).toFixed(2)}</td>
                  <td className="p-2 text-center tabular">{a.fulfilled_by === "partner" ? money(a.cost_price_mad) : "—"}</td>
                  <td className="p-2 text-center tabular text-success">{money(a.margin_mad)}</td>
                  <td className="p-2">{a.fulfilled_by === "partner" ? "Partner florist" : "Fleurinelle"}</td>
                  <td className="p-2 text-muted text-xs">
                    {(a.city_ids || []).length ? `${a.city_ids.length} cities` : "All cities"}
                    {" · "}
                    {(a.occasion_ids || []).length ? `${a.occasion_ids.length} occasions` : "All occasions"}
                  </td>
                  <td className="p-2 text-center tabular">{a.units_sold || 0}</td>
                  <td className="p-2 text-center"><Badge tone={a.is_active === false ? "plain" : "good"}>{a.is_active === false ? "off" : "live"}</Badge></td>
                  <td className="p-2 text-end whitespace-nowrap">
                    <button className="text-muted hover:text-ink me-3" title="Activate / deactivate" data-testid={`extra-toggle-${a.id}`}
                            onClick={() => toggle(a)}><Power size={13} /></button>
                    <button className="text-muted hover:text-danger" data-testid={`extra-delete-${a.id}`}
                            onClick={() => remove(a)}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {data.extras.length === 0 ? <tr><td colSpan={9} className="p-4 text-sm text-muted" data-testid="extras-empty">No extra yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </Panel>

      {draft ? (
        <Drawer testid="extra-drawer" onClose={() => setDraft(null)}
                title={draft.id ? "Edit extra" : "New extra"} subtitle="Shown on the bouquet page and in the basket"
                actions={<button className="btn-primary text-xs px-4" data-testid="extra-save" onClick={save}>Save</button>}>
          <div className="space-y-2">
            <p className="text-xs text-ink font-medium">Name</p>
            {LOCALES.map((l) => (
              <input key={l} className="field text-sm" data-testid={`extra-name-${l}`} placeholder={`${l.toUpperCase()} name`}
                     value={draft.name?.[l] || ""} onChange={(e) => setDraft({ ...draft, name: { ...draft.name, [l]: e.target.value } })} />
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block text-xs">
              <span className="text-ink font-medium">Buyer pays (EUR)</span>
              <input className="field mt-1 tabular" type="number" min="0" step="0.01" data-testid="extra-price"
                     value={draft.price_eur} onChange={(e) => setDraft({ ...draft, price_eur: e.target.value })} />
            </label>
            <label className="block text-xs">
              <span className="text-ink font-medium">Fulfilled by</span>
              <select className="field mt-1" data-testid="extra-fulfilled-by" value={draft.fulfilled_by}
                      onChange={(e) => setDraft({ ...draft, fulfilled_by: e.target.value })}>
                <option value="platform">Fleurinelle — we keep 100%</option>
                <option value="partner">The partner florist — paid the cost price below</option>
              </select>
            </label>
            {draft.fulfilled_by === "partner" ? (
              <label className="block text-xs">
                <span className="text-ink font-medium">Partner is paid (MAD)</span>
                <input className="field mt-1 tabular" type="number" min="0" data-testid="extra-cost"
                       value={draft.cost_price_mad} onChange={(e) => setDraft({ ...draft, cost_price_mad: e.target.value })} />
                <span className="text-muted" data-testid="extra-cost-eur">{eurOf(fx, draft.cost_price_mad)}</span>
              </label>
            ) : null}
            <label className="block text-xs">
              <span className="text-ink font-medium">Order in the list</span>
              <input className="field mt-1 tabular" type="number" data-testid="extra-sort"
                     value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })} />
            </label>
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-xs text-ink">
              <input type="checkbox" checked={!!draft.is_active} data-testid="extra-active"
                     onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active
            </label>
            <label className="flex items-center gap-2 text-xs text-ink">
              <input type="checkbox" checked={!!draft.is_celebratory} data-testid="extra-celebratory"
                     onChange={(e) => setDraft({ ...draft, is_celebratory: e.target.checked })} />
              Celebratory — hidden on sympathy occasions
            </label>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-ink font-medium">Photo</p>
            {draft.image_url ? <img src={draft.image_url} alt="" className="w-24 aspect-square object-cover rounded-sm" /> : null}
            <input type="file" accept="image/*" className="text-xs" data-testid="extra-image"
                   onChange={(e) => { if (e.target.files?.[0]) setPendingPhoto(e.target.files[0]); e.target.value = ""; }} />
            {draft.image_url ? (
              <button className="btn-ghost text-[11px]" data-testid="extra-image-clear"
                      onClick={() => setDraft((d) => ({ ...d, image_url: "" }))}>Remove</button>
            ) : null}
          </div>
          <div className="space-y-2">
            <p className="text-xs text-ink font-medium">Cities — empty means every city</p>
            <Chips options={data.cities} selected={draft.city_ids || []} testid="extra-cities"
                   onChange={(v) => setDraft({ ...draft, city_ids: v })} />
            <p className="text-xs text-ink font-medium pt-2">Occasions — empty means every occasion</p>
            <Chips options={data.occasions} selected={draft.occasion_ids || []} testid="extra-occasions"
                   onChange={(v) => setDraft({ ...draft, occasion_ids: v })} />
          </div>
        </Drawer>
      ) : null}
      {pendingPhoto ? (
        <ImageCropModal file={pendingPhoto} aspect={1} title="Frame the extra's photo"
                        onCancel={() => setPendingPhoto(null)} onDone={upload} />
      ) : null}
    </div>
  );
}
